import {
  clampHorizontalScrollLeft,
  snapHorizontalScrollLeftToEdge
} from '../../utils/horizontalScrollEdge';

import type { ScrollController } from './types';

const LERP_DONE_PX = 0.5;

export type LerpScrollHandle = {
  controller: ScrollController;
  cancelAnimation: () => void;
  syncTargetFromContainer: () => void;
  getTargetScrollLeft: () => number;
  setTargetScrollLeft: (left: number) => void;
  scheduleLerp: () => void;
};

export const createLerpScroll = (
  container: HTMLElement,
  smoothFactor: number
): LerpScrollHandle => {
  let targetScrollLeft = container.scrollLeft;
  let animationFrameId: number | null = null;

  const getScrollMetrics = () => ({
    scrollWidth: container.scrollWidth,
    clientWidth: container.clientWidth
  });

  const cancelAnimation = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    targetScrollLeft = container.scrollLeft;
  };

  const getAdaptiveSmoothFactor = (diff: number) => {
    const distance = Math.abs(diff);
    if (distance < 8) {
      return Math.min(0.4, smoothFactor * 3);
    }
    if (distance < 24) {
      return Math.min(0.24, smoothFactor * 2);
    }
    return smoothFactor;
  };

  const syncTargetFromContainer = () => {
    if (animationFrameId === null) {
      targetScrollLeft = container.scrollLeft;
    }
  };

  const finishLerp = () => {
    const { scrollWidth, clientWidth } = getScrollMetrics();
    const nextScrollLeft = snapHorizontalScrollLeftToEdge(
      targetScrollLeft,
      scrollWidth,
      clientWidth
    );

    container.scrollLeft = nextScrollLeft;
    targetScrollLeft = container.scrollLeft;
    animationFrameId = null;
  };

  const runLerpStep = () => {
    const { scrollWidth, clientWidth } = getScrollMetrics();
    targetScrollLeft = clampHorizontalScrollLeft(
      targetScrollLeft,
      scrollWidth,
      clientWidth
    );

    const diff = targetScrollLeft - container.scrollLeft;

    if (Math.abs(diff) < LERP_DONE_PX) {
      finishLerp();
      return;
    }

    const prevScrollLeft = container.scrollLeft;
    container.scrollLeft += diff * getAdaptiveSmoothFactor(diff);

    if (
      container.scrollLeft === prevScrollLeft &&
      Math.abs(diff) > LERP_DONE_PX
    ) {
      finishLerp();
      return;
    }

    animationFrameId = requestAnimationFrame(runLerpStep);
  };

  const scheduleLerp = () => {
    if (animationFrameId === null) {
      animationFrameId = requestAnimationFrame(runLerpStep);
    }
  };

  const scroll = (shift: number) => {
    cancelAnimation();
    const { scrollWidth, clientWidth } = getScrollMetrics();
    targetScrollLeft = clampHorizontalScrollLeft(
      shift,
      scrollWidth,
      clientWidth
    );
    scheduleLerp();
  };

  const scrollTo = (left: number) => scroll(left);
  const scrollBy = (delta: number) => scroll(targetScrollLeft + delta);

  return {
    controller: { scrollTo, scrollBy },
    cancelAnimation,
    syncTargetFromContainer,
    getTargetScrollLeft: () => targetScrollLeft,
    setTargetScrollLeft: (left: number) => {
      targetScrollLeft = left;
    },
    scheduleLerp
  };
};
